// src/features/locations/LocationTile.jsx
//
// Slice U4 — single tile in the portfolio Tiles grid. Faithful port of
// the legacy monolith's `LocationGridCard` (Ripple Workplace App.html
// line 7541), renamed to `LocationTile` to match the post-split naming
// convention (sites → locations).
//
// Visual contract:
//   - 160px tall photo area at the top, full bleed.
//   - When `imageUrl` is set: <img object-fit:cover>, lazy loaded.
//   - When null: a stage-tinted gradient placeholder showing the first
//     two letters of the market name.
//   - Stage badge top-right (overlaid on photo).
//   - Online Date ribbon bottom-left for stage="build".
//   - Info section under the photo:
//       · Name row: market + alias (flex, space-between) with Space Fit
//         verdict pill right-aligned when verdict is non-null.
//       · Geo line: country · region + optional ◆ Hub chip when isHub.
//       · Stat line (operational only): sqft · seats · util % (util
//         omitted when avgUtilPct is null).
//   - Hover: lifts (-2px translateY) and elevates the shadow.
//   - Closed-stage: dims to 55% opacity (matches LocationCard).
//   - Click and Enter/Space keyboard activation (a11y parity with
//     LocationCard).

import React from "react";
import { STAGES, T } from "../../app/tokens.js";
import { formatFootprintSqftFull as fmtSqft, fmtDateShort } from "../../utils/format.js";
import { getDisplayedSqft, getAnalyticalSqft } from "../../utils/locationSqftDerived.js";
import { getMAndATagName } from "../../utils/mAndA.js";
import {
  computeSpaceFit,
  SPACE_FIT_COLOR,
  SPACE_FIT_BG,
  SPACE_FIT_BORDER,
} from "../../utils/spaceFitVerdict.js";
import { USABLE_CAPACITY_STAGES } from "../../utils/capacityStages.js";
import { computeOfficeSpaceFit, SPACE_FIT_COLOR as FIT_COLOR, SPACE_FIT_BG as FIT_BG, SPACE_FIT_BORDER as FIT_BORDER } from "../../services/officeSeatFitPresentation.js";

// Micro-label shown next to the "—" for non-usable-stage offices.
// Mirrors the SPACE_FIT_STAGE_NOTE in OfficesPage.jsx (list view).
const SPACE_FIT_STAGE_NOTE = {
  build:   "In build",
  vacated: "Vacated",
};


// Hub chip colours — routed through T tokens (hubChipBg / hubChipBorder / hubChipText).
const HUB_CHIP = {
  bg:     T.hubChipBg,
  border: T.hubChipBorder,
  text:   T.hubChipText,
};

export function LocationTile({ location, onClick, selected = false }) {
  const stage       = STAGES[location.stage] || STAGES.closed;
  const sqft        = getDisplayedSqft(location, null) != null ? fmtSqft(getDisplayedSqft(location, null)) : null;
  const seats       = location.installedCapacity
    ? Number(location.installedCapacity).toLocaleString()
    : null;
  const isClosed    = location.stage === "closed";
  const isOperational = location.stage === "operational";

  // Space Fit verdict pill — only computed for usable-capacity stages (operational/dormant).
  // Build and vacated offices are not scored even when seats+people data is present.
  const sf = getAnalyticalSqft(location);
  const sfSeats = location.installedCapacity != null ? Number(location.installedCapacity) : null;
  const sfPeople = location.latestHeadcount?.totalCount != null ? Number(location.latestHeadcount.totalCount) : null;
  const { verdict } = location.seatFitToday ? computeOfficeSpaceFit(location) : USABLE_CAPACITY_STAGES.includes(location.stage)
    ? computeSpaceFit(sf, sfSeats, sfPeople)
    : { verdict: null };
  const currentFit = location.seatFitToday ? computeOfficeSpaceFit(location) : null;
  const spaceFitStageNote = SPACE_FIT_STAGE_NOTE[location.stage] ?? null;

  // Utilization — only when avgUtilPct is non-null (null = no badge reader / no data).
  const utilPct = location.avgUtilPct != null ? Math.round(location.avgUtilPct * 100) : null;

  const handleKeyDown = (e) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <div
      data-testid={`location-tile-${location.id}`}
      data-portfolio-tile="true"
      onClick={onClick}
      onMouseDown={(event) => {
        if (event.button !== 0) return;
        // Default focus can scroll a partially visible card into view before
        // the drawer opens. Keep keyboard focus without moving the collection.
        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
      }}
      onKeyDown={handleKeyDown}
      role="button"
      aria-pressed={selected}
      tabIndex={0}
      aria-label={`${location.market}${location.locationAlias ? " " + location.locationAlias : ""} - ${location.stage}`}
      className="wos-card-interactive"
      style={{
        background: selected ? T.rowActive : T.card,
        borderRadius: 10,
        border: `1px solid ${selected ? T.blue : T.border}`,
        overflow: "hidden",
        cursor: "pointer",
        opacity: isClosed ? 0.55 : 1,
        display: "flex",
        flexDirection: "column",
      }}
      onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${T.blue}`; }}
      onBlur={(e)  => { e.currentTarget.style.boxShadow = ""; }}
    >
      {/* Image area — full-bleed photo or stage-tinted placeholder */}
      <div
        data-testid="tile-photo"
        style={{ position: "relative", width: "100%", height: 160, flexShrink: 0, overflow: "hidden" }}
      >
        {location.imageUrl ? (
          <img
            src={location.imageUrl}
            alt={location.market || location.id}
            loading="lazy"
            data-testid="tile-img"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            data-testid="tile-placeholder"
            style={{
              width: "100%", height: "100%",
              background: `linear-gradient(135deg, ${T.hdrFrom} 0%, ${T.hdrTo} 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <span style={{ fontSize: T.fsXl6, color: T.whiteHalf, fontWeight: T.fwBold, letterSpacing: 1 }}>
              {location.market ? location.market.slice(0, 2).toUpperCase() : "??"}
            </span>
          </div>
        )}

        {/* Stage badge — top-left (overlaid; OFFICES-TILE-STAGE-PILL-LEFT).
            LSE-TILE-STAGE-PILL-TINT: bg from stage.bg (canonical soft tint),
            dark text for legibility over any photo, stage.color dot matches
            the 8px dot in StageBadge's dropdown options (StageBadge.jsx:200). */}
        <span style={{
          position: "absolute", top: 8, left: 8,
          background: stage.bg,
          color: T.dark,
          fontSize: T.fsXxs, fontWeight: T.fwBold,
          padding: "3px 7px", borderRadius: 6,
          letterSpacing: 0.3,
          display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: stage.color, flexShrink: 0 }} />
          {stage.label.toUpperCase()}
        </span>

        {/* Online Date overlay — bottom-left, build stage only.
            FDOB-TO-ONLINEDATE-CONSOLIDATE-S1: replaces the retired FDOB
            ribbon; reads onlineDate instead. */}
        {location.stage === "build" && location.onlineDate && (
          <span style={{
            position: "absolute", bottom: 8, left: 10,
            background: "rgba(0,0,0,0.50)",
            color: T.white,
            fontSize: T.fsXxs, fontWeight: T.fwBold,
            padding: "3px 7px", borderRadius: 6,
            letterSpacing: 0.2,
            backdropFilter: "blur(4px)",
          }}>
            Online {fmtDateShort(location.onlineDate)}
          </span>
        )}
      </div>

      {/* Info section */}
      <div style={{ padding: "12px 14px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>

        {/* Name row — market + alias on the left, Space Fit pill on the right */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
          <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.3, minWidth: 0 }}>
            {location.market}
            {location.locationAlias && (
              <span style={{ fontWeight: T.fwRegular, color: T.mid, fontSize: T.fsBase }}>
                {" · "}{location.locationAlias}
              </span>
            )}
            {location.mAndA && (
              <span style={{ fontWeight: T.fwRegular, color: T.mid, fontSize: T.fsBase }}>
                {" · "}{getMAndATagName(location.mAndA)}
              </span>
            )}
          </div>
          {verdict ? (
            <span
              data-testid={`tile-space-fit-pill-${location.id}`}
              title={currentFit?.disclosure}
              style={{
                display:      "inline-block",
                flexShrink:   0,
                borderRadius: 7,
                padding:      "2px 7px",
                fontSize:     T.fsXs,
                fontWeight:   T.fwBold,
                background:   (currentFit ? FIT_BG : SPACE_FIT_BG)[verdict],
                color:        (currentFit ? FIT_COLOR : SPACE_FIT_COLOR)[verdict],
                border:       `1px solid ${(currentFit ? FIT_BORDER : SPACE_FIT_BORDER)[verdict]}`,
                whiteSpace:   "nowrap",
              }}
            >
              {verdict}{currentFit?.dense ? " · Dense" : ""}
            </span>
          ) : currentFit && ["partial", "no_seats", "error", "loading"].includes(currentFit.status) ? (
            <span title={currentFit.disclosure} style={{ color: T.mid, fontSize: T.fsXs }}>
              {{ partial: "Partial", no_seats: "No seats", error: "Read failed", loading: "Loading…" }[currentFit.status]}
            </span>
          ) : spaceFitStageNote && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <span style={{ color: "var(--v2-light)" }}>—</span>
              <span
                data-testid={`tile-space-fit-note-${location.id}`}
                style={{ fontSize: T.fsXxs5, color: "var(--v2-light)", lineHeight: 1 }}
              >
                {spaceFitStageNote}
              </span>
            </div>
          )}
        </div>

        {/* Geo line — country · region + optional Hub chip */}
        <div style={{ fontSize: T.fsXs, color: T.light, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span>{[location.country, location.region].filter(Boolean).join(" · ")}</span>
          {location.isHub && (
            <span
              data-testid={`tile-hub-chip-${location.id}`}
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                gap:          3,
                background:   HUB_CHIP.bg,
                border:       `1px solid ${HUB_CHIP.border}`,
                color:        HUB_CHIP.text,
                fontSize:     T.fsXxs,
                fontWeight:   T.fwBold,
                padding:      "1px 5px",
                borderRadius: 4,
              }}
            >
              ◆ Hub
            </span>
          )}
        </div>

        {/* Footprint is office-owned display space; other analytics remain operational-only. */}
        {(sqft || (isOperational && seats)) && (
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 4, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            {sqft  && <span>{sqft}</span>}
            {sqft && isOperational && seats && <span style={{ margin: "0 5px" }}>·</span>}
            {isOperational && seats && <span>{seats} seats</span>}
            {isOperational && utilPct != null && (
              <>
                <span style={{ margin: "0 5px" }}>·</span>
                <span
                  data-testid={`tile-util-${location.id}`}
                  style={{ fontWeight: T.fwSemibold }}
                >
                  {utilPct}% util
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
